import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Send, Home, FileText, BookOpen, Play, Plus, Trash2, RefreshCw, Settings } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTextGameStore } from '../../../stores/textGameStore'
import { useSettingsStore } from '../../../stores/settingsStore'
import { GameFileManager } from './TextGameTool/GameFileManager'
import { TemplateManager } from './TextGameTool/TemplateManager'
import { SessionCreateDialog } from './TextGameTool/SessionCreateDialog'
import { GameFileGenerator } from './TextGameTool/GameFileGenerator'
import { StructuredModePanel } from './TextGameTool/StructuredModePanel'
import { CharacterStatusDisplay } from './TextGameTool/CharacterStatusDisplay'
import type { GameFile, GameMode } from '@creanizer/types'

interface Template {
  id: string
  name: string
  systemPrompt: string
  description?: string
  createdAt: string
  updatedAt: string
}

function TextGameTool() {
  const [activeTab, setActiveTab] = useState<'play' | 'files' | 'templates'>('play')
  const [showSessionCreate, setShowSessionCreate] = useState(false)
  const [showFileEditor, setShowFileEditor] = useState(false)
  const [editingGameFile, setEditingGameFile] = useState<GameFile | null>(null)
  const [showFileGenerator, setShowFileGenerator] = useState(false)
  const [templates, setTemplates] = useState<Template[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('textgame-templates') || '[]')
    } catch {
      return []
    }
  })
  const [inputText, setInputText] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [lastError, setLastError] = useState<string | null>(null)
  const [failedMessageId, setFailedMessageId] = useState<string | null>(null)
  const [failedMessageContent, setFailedMessageContent] = useState<string | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [gameSettings, setGameSettings] = useState({
    responseLength: 'medium' as 'short' | 'medium' | 'long',
    temperature: 0.8,
    includeStats: true,
    useGemini3Mode: false,
    useGemini3FlashMode: false
  })
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  const {
    sessions,
    activeSession,
    gameFiles,
    isLoading,
    error,
    createSession,
    loadSession,
    deleteSession,
    saveGameFile,
    loadGameFile,
    deleteGameFile,
    sendAction,
    retryUserMessage,
    resetGame,
    exportSession
  } = useTextGameStore()
  
  const { activeApiKey } = useSettingsStore()
  const currentApiKey = activeApiKey
  
  // 自動スクロール
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [activeSession?.messages])
  
  // テンプレート保存
  const handleSaveTemplate = (template: Template) => {
    const existingIndex = templates.findIndex(t => t.id === template.id)
    const updatedTemplates = existingIndex >= 0
      ? templates.map(t => t.id === template.id ? template : t)
      : [...templates, template]
    setTemplates(updatedTemplates)
    localStorage.setItem('textgame-templates', JSON.stringify(updatedTemplates))
  }
  
  const handleDeleteTemplate = (id: string) => {
    const updatedTemplates = templates.filter(t => t.id !== id)
    setTemplates(updatedTemplates)
    localStorage.setItem('textgame-templates', JSON.stringify(updatedTemplates))
  }
  
  // ゲームファイル管理
  const handleCreateGameFile = () => {
    setShowFileGenerator(true)
  }
  
  const handleEditGameFile = (file: GameFile) => {
    setEditingGameFile(file)
    setShowFileEditor(true)
  }
  
  const handleDuplicateGameFile = (file: GameFile) => {
    const newFile: GameFile = {
      ...file,
      id: `game_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      name: `${file.name} (コピー)`,
      metadata: {
        ...file.metadata,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    }
    saveGameFile(newFile)
  }
  
  const handleExportGameFile = (file: GameFile) => {
    const data = JSON.stringify(file, null, 2)
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/[^a-zA-Z0-9]/g, '_')}.json`
    a.click()
    URL.revokeObjectURL(url)
  }
  
  const handleImportGameFile = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      
      try {
        const text = await file.text()
        const gameFile = JSON.parse(text) as GameFile
        gameFile.id = `game_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        saveGameFile(gameFile)
        alert('ゲームファイルをインポートしました')
      } catch (error) {
        alert('インポートに失敗しました')
      }
    }
    input.click()
  }
  
  // セッション作成
  const handleStartWithFile = (sessionName: string, gameFile: GameFile) => {
    createSession(sessionName, 'structured', gameFile.id)
    setShowSessionCreate(false)
    setActiveTab('play')
  }
  
  const handleStartWithTemplate = (sessionName: string, template: Template) => {
    // フリーモードのゲームファイルを作成
    const gameFile: GameFile = {
      id: `game_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      name: sessionName,
      description: template.description || '',
      version: '1.0.0',
      mode: 'free',
      freeMode: {
        systemPrompt: template.systemPrompt
      },
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: 'ユーザー',
        language: 'ja'
      }
    }
    const savedFile = saveGameFile(gameFile)
    createSession(sessionName, 'free', savedFile.id)
    setShowSessionCreate(false)
    setActiveTab('play')
  }
  
  const handleStartFreeMode = (sessionName: string, systemPrompt: string) => {
    // フリーモードのゲームファイルを作成
    const gameFile: GameFile = {
      id: `game_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      name: sessionName,
      description: '',
      version: '1.0.0',
      mode: 'free',
      freeMode: {
        systemPrompt
      },
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: 'ユーザー',
        language: 'ja'
      }
    }
    const savedFile = saveGameFile(gameFile)
    createSession(sessionName, 'free', savedFile.id)
    setShowSessionCreate(false)
    setActiveTab('play')
  }
  
  const handleStartSessionFromFile = (file: GameFile) => {
    const sessionName = prompt('セッション名を入力してください', `${file.name} - セッション`)
    if (sessionName) {
      createSession(sessionName, file.mode, file.id)
      setActiveTab('play')
    }
  }
  
  const handleStartSessionFromTemplate = (template: Template) => {
    const sessionName = prompt('セッション名を入力してください', `${template.name} - セッション`)
    if (sessionName) {
      handleStartWithTemplate(sessionName, template)
    }
  }
  
  // メッセージ送信
  const handleSendMessage = async () => {
    const messageText = inputText
    if (!messageText.trim() || !activeSession || isProcessing) return

    setIsProcessing(true)
    setLastError(null)
    setFailedMessageId(null)
    setFailedMessageContent(null)
    setInputText('')

    try {
      if (!currentApiKey) {
        throw new Error('APIキーが設定されていません。設定画面からGemini APIキーを登録してください。')
      }

      console.log('Sending action:', messageText, 'Session:', activeSession)
      const result = await sendAction(messageText, currentApiKey.key, {
        temperature: gameSettings.temperature,
        responseLength: gameSettings.responseLength,
        useGemini3Mode: gameSettings.useGemini3Mode,
        useGemini3FlashMode: gameSettings.useGemini3FlashMode
      })

      console.log('Action result:', result)

      if (!result.success) {
        console.error('Action failed:', result)
        throw new Error(result.message || 'ゲームの処理に失敗しました')
      }

      if (result.gameEnd?.ended) {
        if (result.gameEnd.win) {
          alert('🎉 おめでとうございます！ゲームをクリアしました！')
        } else {
          alert('💀 ゲームオーバー... もう一度挑戦してみましょう！')
        }
      }
    } catch (error) {
      console.error('Send message error:', error)
      const errorMessage = error instanceof Error ? error.message : 'エラーが発生しました'
      setLastError(errorMessage)

      // 失敗したメッセージ内容を保存（再送信用）
      setFailedMessageContent(messageText)
    } finally {
      setIsProcessing(false)
    }
  }
  
  // メッセージ再送信
  const handleRetryMessage = async () => {
    if (!activeSession || isProcessing || !currentApiKey || !failedMessageContent) return

    setIsProcessing(true)
    setLastError(null)

    try {
      console.log('Retrying action:', failedMessageContent, 'Session:', activeSession)
      const result = await sendAction(failedMessageContent, currentApiKey.key, {
        temperature: gameSettings.temperature,
        responseLength: gameSettings.responseLength,
        useGemini3Mode: gameSettings.useGemini3Mode,
        useGemini3FlashMode: gameSettings.useGemini3FlashMode
      })

      console.log('Retry result:', result)

      if (!result.success) {
        console.error('Retry failed:', result)
        throw new Error(result.message || 'ゲームの処理に失敗しました')
      }

      // 成功したので失敗メッセージをクリア
      setFailedMessageContent(null)

      if (result.gameEnd?.ended) {
        if (result.gameEnd.win) {
          alert('🎉 おめでとうございます！ゲームをクリアしました！')
        } else {
          alert('💀 ゲームオーバー... もう一度挑戦してみましょう！')
        }
      }
    } catch (error) {
      console.error('Retry message error:', error)
      const errorMessage = error instanceof Error ? error.message : 'エラーが発生しました'
      setLastError(errorMessage)
      // failedMessageContentは保持したまま（再度リトライできるように）
    } finally {
      setIsProcessing(false)
    }
  }
  
  // セッション削除
  const handleDeleteSession = (sessionId: string) => {
    if (confirm('このセッションを削除してもよろしいですか？')) {
      deleteSession(sessionId)
      if (activeSession?.id === sessionId) {
        // 削除したセッションがアクティブだった場合、クリア
        loadSession('')
      }
    }
  }
  
  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <span className="text-4xl">🎮</span>
          テキストゲームツール
          <span className="text-sm text-orange-600 font-normal ml-2">[開発中]</span>
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(true)}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            設定
          </button>
          <Link
            to="/"
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            ホームに戻る
          </Link>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="play">
            <Play className="w-4 h-4 mr-2" />
            プレイ
          </TabsTrigger>
          <TabsTrigger value="files">
            <FileText className="w-4 h-4 mr-2" />
            ゲームファイル
          </TabsTrigger>
          <TabsTrigger value="templates">
            <BookOpen className="w-4 h-4 mr-2" />
            テンプレート
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="play" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* サイドバー：セッション管理 */}
            <div className="lg:col-span-1">
              <div className="card">
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold">セッション</h2>
                    <button
                      onClick={() => setShowSessionCreate(true)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="新規セッション"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {sessions.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      セッションがありません
                    </div>
                  ) : (
                    <div className="p-2">
                      {sessions.map((session) => (
                        <div
                          key={session.id}
                          className={`group p-3 rounded-lg mb-2 cursor-pointer transition-colors ${
                            activeSession?.id === session.id
                              ? 'bg-blue-50 border-blue-200 border'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div
                              className="flex-1 min-w-0"
                              onClick={() => loadSession(session.id)}
                            >
                              <div className="font-medium text-sm truncate">
                                {session.title}
                              </div>
                              <div className="text-xs text-gray-500">
                                {session.mode === 'free' ? 'フリー' : '構造化'} • ターン {session.gameState.turn}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteSession(session.id)
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* メインエリア：チャット */}
            <div className="lg:col-span-3">
              {!activeSession ? (
                <div className="card">
                  <div className="p-16 text-center">
                    <div className="text-6xl mb-4">🎮</div>
                    <h2 className="text-xl font-semibold mb-2">
                      ゲームセッションを開始
                    </h2>
                    <p className="text-gray-600 mb-6">
                      新規セッションを作成するか、既存のセッションを選択してください
                    </p>
                    <Button onClick={() => setShowSessionCreate(true)}>
                      <Play className="w-5 h-5 mr-2" />
                      新しいセッションを開始
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="card flex flex-col" style={{ height: 'calc(100vh - 300px)', minHeight: '500px' }}>
                  <div className="p-4 border-b bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="font-semibold text-lg">{activeSession.title}</h2>
                        <div className="text-sm text-gray-600">
                          {activeSession.mode === 'free' ? 'フリーモード' : '構造化モード'} • 
                          ターン {activeSession.gameState.turn}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* ゲームパラメータ表示（構造化モード） */}
                  {activeSession.mode === 'structured' && activeSession.gameState.parameters && Object.keys(activeSession.gameState.parameters).length > 0 && (
                    <div className="px-4 py-3 border-b bg-gray-50">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {(() => {
                          // ゲームファイルからパラメータ定義を取得
                          const gameFile = activeSession.gameFileId ? gameFiles.find(f => f.id === activeSession.gameFileId) : undefined
                          const paramDefinitions = gameFile?.structuredMode?.parameters || []
                          
                          return Object.entries(activeSession.gameState.parameters).map(([key, value]) => {
                            // パラメータ定義から表示名を取得
                            const definition = paramDefinitions.find(p => p.name === key)
                            const displayName = definition?.displayName || key
                            
                            // 数値の場合は見やすく表示
                            let displayValue: string = String(value)
                            if (typeof value === 'number') {
                              displayValue = value.toLocaleString()
                              // 最大値がある場合は表示
                              if (definition?.max !== undefined) {
                                displayValue = `${displayValue} / ${definition.max.toLocaleString()}`
                              }
                            } else if (typeof value === 'boolean') {
                              displayValue = value ? '○' : '×'
                            } else if (Array.isArray(value)) {
                              displayValue = value.join(', ')
                            }
                            
                            // 重要パラメータはハイライト
                            const isCritical = definition?.critical
                            
                            return (
                              <div 
                                key={key} 
                                className={`bg-white rounded-lg px-3 py-2 border ${isCritical ? 'border-orange-400 bg-orange-50' : ''}`}
                              >
                                <div className="text-xs text-gray-500">{displayName}</div>
                                <div className={`text-sm font-semibold ${isCritical ? 'text-orange-600' : ''}`}>
                                  {displayValue}
                                </div>
                              </div>
                            )
                          })
                        })()}
                      </div>
                    </div>
                  )}
                  
                  {/* キャラクターステータス表示 */}
                  {activeSession.gameState.characters && (
                    <div className="px-4 py-2 border-b bg-white">
                      <CharacterStatusDisplay 
                        gameState={activeSession.gameState}
                        alwaysShowParty={true}
                      />
                    </div>
                  )}
                  
                  <div className="flex-1 overflow-y-auto p-4">
                    {activeSession.messages.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        ゲームを開始するには、下のテキストボックスにコマンドを入力してください
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {activeSession.messages.map((message, index) => {
                          const isLastUserMessage = message.role === 'user' && 
                            index === activeSession.messages.length - 1
                          const canRetry = message.role === 'user' && 
                            index === activeSession.messages.length - 2 &&
                            activeSession.messages[index + 1]?.role === 'game'
                          
                          return (
                            <div
                              key={message.id}
                              className={`group ${
                                message.role === 'user'
                                  ? 'flex justify-end'
                                  : 'flex justify-start'
                              }`}
                            >
                              <div className="relative max-w-3xl">
                                <div
                                  className={`p-4 rounded-lg ${
                                    message.role === 'user'
                                      ? 'bg-blue-100 text-blue-900'
                                      : 'bg-gray-100 text-gray-900'
                                  }`}
                                >
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      img: ({node, ...props}) => (
                                        <img {...props} className="max-w-full h-auto rounded-lg my-2" alt={props.alt || ''} />
                                      ),
                                      a: ({node, ...props}) => (
                                        <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" />
                                      )
                                    }}
                                  >
                                    {message.content}
                                  </ReactMarkdown>
                                </div>
                                
                                {/* 再送信ボタン（エラー時のみ表示） */}
                                {isLastUserMessage && lastError && failedMessageContent && (
                                  <button
                                    onClick={() => handleRetryMessage()}
                                    disabled={isProcessing}
                                    className="absolute -bottom-2 right-0 opacity-0 group-hover:opacity-100 transition-opacity bg-white border rounded-full p-1.5 hover:bg-gray-50 disabled:opacity-50"
                                    title="このメッセージを再送信"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 border-t bg-gray-50">
                    {/* エラー表示 */}
                    {lastError && failedMessageContent && (
                      <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
                        <span>{lastError}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRetryMessage()}
                          disabled={isProcessing}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          再送信
                        </Button>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            handleSendMessage()
                          }
                        }}
                        placeholder="アクションを入力..."
                        disabled={isProcessing || isLoading}
                        className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                      <Button
                        onClick={() => handleSendMessage()}
                        disabled={!inputText.trim() || isProcessing || isLoading}
                      >
                        {isProcessing || isLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                            処理中...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            送信
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="files" className="mt-6">
          <GameFileManager
            gameFiles={gameFiles}
            onCreateNew={handleCreateGameFile}
            onEdit={handleEditGameFile}
            onDelete={deleteGameFile}
            onDuplicate={handleDuplicateGameFile}
            onExport={handleExportGameFile}
            onImport={handleImportGameFile}
            onStartSession={handleStartSessionFromFile}
          />
        </TabsContent>
        
        <TabsContent value="templates" className="mt-6">
          <TemplateManager
            templates={templates}
            onSave={handleSaveTemplate}
            onDelete={handleDeleteTemplate}
            onStartSession={handleStartSessionFromTemplate}
          />
        </TabsContent>
      </Tabs>
      
      {/* モーダル：セッション作成 */}
      {showSessionCreate && (
        <SessionCreateDialog
          gameFiles={gameFiles}
          templates={templates}
          onStartWithFile={handleStartWithFile}
          onStartWithTemplate={handleStartWithTemplate}
          onStartFreeMode={handleStartFreeMode}
          onCreateNewFile={() => {
            setShowSessionCreate(false)
            setActiveTab('files')
            handleCreateGameFile()
          }}
          onClose={() => setShowSessionCreate(false)}
        />
      )}
      
      {/* モーダル：ゲームファイル生成 */}
      {showFileGenerator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <GameFileGenerator
              onGenerated={(file) => {
                setEditingGameFile(file)
                setShowFileGenerator(false)
                setShowFileEditor(true)
              }}
            />
            <Button
              onClick={() => setShowFileGenerator(false)}
              variant="outline"
              className="mt-4 w-full"
            >
              キャンセル
            </Button>
          </div>
        </div>
      )}
      
      {/* モーダル：ゲームファイル編集 */}
      {showFileEditor && editingGameFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <StructuredModePanel
              onStartGame={(file) => {
                saveGameFile(file)
                setShowFileEditor(false)
                setEditingGameFile(null)
                handleStartSessionFromFile(file)
              }}
              savedGameFiles={gameFiles}
              onSaveGameFile={(file) => {
                saveGameFile(file)
                setShowFileEditor(false)
                setEditingGameFile(null)
              }}
              onLoadGameFile={(file) => {
                setEditingGameFile(file)
              }}
              initialGameFile={editingGameFile}
            />
            <Button
              onClick={() => {
                setShowFileEditor(false)
                setEditingGameFile(null)
              }}
              variant="outline"
              className="mt-4 w-full"
            >
              閉じる
            </Button>
          </div>
        </div>
      )}

      {/* モーダル：ゲーム設定 */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">ゲーム設定</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  応答の長さ
                </label>
                <select
                  value={gameSettings.responseLength}
                  onChange={(e) => setGameSettings({
                    ...gameSettings,
                    responseLength: e.target.value as 'short' | 'medium' | 'long'
                  })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="short">短い</option>
                  <option value="medium">標準</option>
                  <option value="long">長い</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  創造性レベル（Temperature: {gameSettings.temperature}）
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="2.0"
                  step="0.1"
                  value={gameSettings.temperature}
                  onChange={(e) => setGameSettings({
                    ...gameSettings,
                    temperature: parseFloat(e.target.value)
                  })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>正確</span>
                  <span>バランス</span>
                  <span>創造的</span>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={gameSettings.includeStats}
                    onChange={(e) => setGameSettings({
                      ...gameSettings,
                      includeStats: e.target.checked
                    })}
                  />
                  <span className="text-sm">ステータス表示を含める</span>
                </label>
              </div>

              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium mb-2">AIモデル設定</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      id="useGemini3FlashMode"
                      type="checkbox"
                      checked={gameSettings.useGemini3FlashMode}
                      onChange={(e) => setGameSettings({
                        ...gameSettings,
                        useGemini3FlashMode: e.target.checked,
                        useGemini3Mode: e.target.checked ? false : gameSettings.useGemini3Mode
                      })}
                    />
                    <span className="text-sm">Gemini 3 Flash モード（試験版）</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      id="useGemini3Mode"
                      type="checkbox"
                      checked={gameSettings.useGemini3Mode}
                      onChange={(e) => setGameSettings({
                        ...gameSettings,
                        useGemini3Mode: e.target.checked,
                        useGemini3FlashMode: e.target.checked ? false : gameSettings.useGemini3FlashMode
                      })}
                    />
                    <span className="text-sm">Gemini 3 Pro モード（有料APIのみ・試験版）</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  未選択時は Gemini 2.5 Pro を使用します
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setGameSettings({
                    responseLength: 'medium',
                    temperature: 0.8,
                    includeStats: true,
                    useGemini3Mode: false,
                    useGemini3FlashMode: false
                  })
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                リセット
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TextGameTool
export { TextGameTool }
